import axios from "axios";
import { NotImplementedError, UnauthorizedError } from "../../errors";
import { PromiseHandler } from "../../handlers";
import { validateFilterStructure, FilterValueTypes, FilterTypes, parseIdFilter, parseInclusionFilter } from "../../handlers/filter";
import { ExperimentBadRequestError, ExperimentNotFoundError, ConfigurationMetaValidationError } from "./errors";
import { validators } from "./validators";
import { ExperimentsHoldouts } from "./holdouts";
import { isValidStatusTransition, getValidNextStatuses } from "../../utils/experiments";
import { experimentEntityFromService, experimentFromService, experimentToService, layerConfigurationFromService, configurationMetaFromService, configurationMetaVariableToService, configurationMetaTriggerToService, configurationMetaConstraintToService, experimentLayerMetaFromService, experimentLayerMetaToService, gitHubTeamFromService, layerAvailabilityFromService, switchbackSchedulePreviewFromService } from "../../models/experiments";
import { buildConfigurationValues, flattenConfigurationMetaVariables } from "./configValueSerialization";
export { ExperimentBadRequestError, ExperimentNotFoundError, ConfigurationMetaValidationError };
export { buildConfigurationValues, parseConfigurationValue, flattenConfigurationMetaVariables, serializeValue } from "./configValueSerialization";
export { compileConfigValuePattern } from "./configurationMetaValidation";
class Experiments extends PromiseHandler {
    constructor(sdk) {
        // Not atomized: an atom cache hit on an ID would skip the advertiserId filter.
        super(sdk, "experiments", { atomize: false });
        this.validators = validators;
        this.configurationsPromise = null;
        // GitHub org teams are static reference data with no user-editable "own
        // writes" from within this app (unlike configurationMeta), so caching for
        // the life of the SDK instance -- the same way getDefaultConfigurations
        // does -- is safe and avoids refetching on every render of an admin form.
        this.githubTeamsPromise = null;
        this.collectionUrl = `${this.sdk.urls.baseAPIUrl}/experiments`;
        this.singleUrl = `${this.sdk.urls.baseAPIUrl}/experiment`;
        this.configurationsUrl = `${this.sdk.urls.baseAPIUrl}/experiment-layers`;
        this.entitiesUrl = `${this.sdk.urls.baseAPIUrl}/experiment-entities`;
        this.configurationMetaUrl = `${this.sdk.urls.baseAPIUrl}/configuration-meta`;
        this.experimentLayerMetaUrl = `${this.sdk.urls.baseAPIUrl}/experiment-layer-meta`;
        this.layerAvailabilityUrl = `${this.sdk.urls.baseAPIUrl}/experiment-layer-availability`;
        this.switchbackPreviewUrl = `${this.sdk.urls.baseAPIUrl}/experiment-switchback-preview`;
        this.githubTeamsUrl = `${this.sdk.urls.baseAPIUrl}/github-teams`;
        this.holdouts = new ExperimentsHoldouts(sdk);
    }
    /**
     * Fetches configuration meta — the grouping-set tree of typed, optionally
     * conditional parameter definitions — for the given layers, or all layers
     * with any configuration meta defined if omitted. Not cached: this backs
     * the management UI, which needs to see its own writes reflected.
     * GET /campaign-management/v1/configuration-meta
     */
    getConfigurationMeta(layers) {
        return axios
            .get(this.configurationMetaUrl, {
            params: layers?.length ? { layers: layers.join(",") } : undefined,
            headers: { "Content-Type": "application/json" }
        })
            .then(({ data: res }) => {
            const result = {};
            for (const [layer, meta] of Object.entries(res.data || {})) {
                const configurationMeta = configurationMetaFromService(layer, meta);
                result[configurationMeta.layer] = configurationMeta;
            }
            return result;
        });
    }
    /**
     * Creates (variable.id unset) or updates (variable.id set) a single
     * configuration meta variable, associating it with an existing grouping
     * set (groupingSetId) or leaving it in the layer's global/always-present
     * set (omitted). Never creates or deletes a grouping set as a side
     * effect — see upsertConfigurationMetaGroupingSet.
     * POST /campaign-management/v1/configuration-meta/variables
     */
    upsertConfigurationMetaVariable(layer, groupingSetId, variable) {
        return axios
            .post(`${this.configurationMetaUrl}/variables`, {
            layer,
            grouping_set_id: groupingSetId,
            variable: configurationMetaVariableToService(variable)
        }, { headers: { "Content-Type": "application/json" } })
            .then(({ data: res }) => res.data);
    }
    /**
     * Removes a single configuration meta variable. If other grouping sets
     * were triggered by this variable's value, deleting it removes those
     * grouping sets too (and everything nested beneath them).
     * DELETE /campaign-management/v1/configuration-meta/variables/:id
     */
    deleteConfigurationMetaVariable(id) {
        return axios
            .delete(`${this.configurationMetaUrl}/variables/${id}`, {
            headers: { "Content-Type": "application/json" }
        })
            .then(() => undefined);
    }
    /**
     * Creates (id unset) or updates (id set) a grouping set's trigger.
     * POST /campaign-management/v1/configuration-meta/grouping-sets
     */
    upsertConfigurationMetaGroupingSet(layer, id, trigger) {
        return axios
            .post(`${this.configurationMetaUrl}/grouping-sets`, {
            layer,
            id,
            trigger: configurationMetaTriggerToService(trigger)
        }, { headers: { "Content-Type": "application/json" } })
            .then(({ data: res }) => res.data);
    }
    /**
     * Removes a grouping set and everything nested beneath it (its own
     * variables, and any grouping set triggered by one of those, and their
     * variables, transitively).
     * DELETE /campaign-management/v1/configuration-meta/grouping-sets/:id
     */
    deleteConfigurationMetaGroupingSet(id) {
        if (!id) {
            return Promise.reject(new ExperimentBadRequestError("Cannot delete the global grouping set"));
        }
        return axios
            .delete(`${this.configurationMetaUrl}/grouping-sets/${id}`, {
            headers: { "Content-Type": "application/json" }
        })
            .then(() => undefined);
    }
    /**
     * Creates (constraint.id unset) or updates (constraint.id set) a
     * constraint gating constraint.targetVariableId's value based on
     * constraint.controllingVariableId's current value.
     * POST /campaign-management/v1/configuration-meta/constraints
     */
    upsertConfigurationMetaConstraint(layer, constraint) {
        return axios
            .post(`${this.configurationMetaUrl}/constraints`, {
            layer,
            constraint: configurationMetaConstraintToService(constraint)
        }, { headers: { "Content-Type": "application/json" } })
            .then(({ data: res }) => res.data);
    }
    /**
     * Removes a single constraint. Neither the constraint's controlling nor
     * target variable is affected.
     * DELETE /campaign-management/v1/configuration-meta/constraints/:id
     */
    deleteConfigurationMetaConstraint(id) {
        return axios
            .delete(`${this.configurationMetaUrl}/constraints/${id}`, {
            headers: { "Content-Type": "application/json" }
        })
            .then(() => undefined);
    }
    /**
     * Fetches default configurations for all experimental layers.
     * GET /campaign-management/v1/experiment-layers
     */
    getDefaultConfigurations() {
        if (this.configurationsPromise) {
            return this.configurationsPromise;
        }
        this.configurationsPromise = axios
            .get(this.configurationsUrl, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(({ data: res }) => {
            const result = {};
            for (const [layer, config] of Object.entries(res.data || {})) {
                result[layer] = layerConfigurationFromService(layer, config);
            }
            return result;
        })
            .catch((err) => {
            this.configurationsPromise = null;
            throw err;
        });
        return this.configurationsPromise;
    }
    /**
     * Inserts a new versioned default configuration for a layer; the new
     * version becomes the active one. Insert-only -- there is no update-in-place
     * or delete. Invalidates the getDefaultConfigurations() cache so the next
     * call reflects this write.
     *
     * `configuration` values may be a nested ConfigValue (an array or 2-tuple)
     * for a key whose configuration meta declares it ARRAY or TUPLE -- this
     * fetches that layer's configuration meta, validates each value against
     * its declared type (recursively, for a comprising type), and serializes
     * it into the single string the backend stores, eliding the comprising-type
     * rows' own keys. A key with no configuration meta defined passes through
     * unvalidated, as before.
     * POST /campaign-management/v1/experiment-layers
     */
    async insertDefaultConfiguration(layer, configuration) {
        const metaByLayer = await this.getConfigurationMeta([layer]);
        const variables = metaByLayer[layer]
            ? flattenConfigurationMetaVariables(metaByLayer[layer])
            : [];
        const serializedConfiguration = buildConfigurationValues(variables, configuration);
        return axios
            .post(this.configurationsUrl, { layer, configuration: serializedConfiguration }, { headers: { "Content-Type": "application/json" } })
            .then(({ data: res }) => {
            this.configurationsPromise = null;
            return res.data;
        });
    }
    /**
     * Fetches metadata for every experiment layer that has a row: display
     * name, permitted eligibility criteria, permitted bucketing entity types,
     * required review groups, and the layer's currently active
     * default-configuration PR. Not cached: this backs the management UI,
     * which needs to see its own writes reflected.
     * GET /campaign-management/v1/experiment-layer-meta
     */
    getExperimentLayerMeta() {
        return axios
            .get(this.experimentLayerMetaUrl, {
            headers: { "Content-Type": "application/json" }
        })
            .then(({ data: res }) => {
            const result = {};
            for (const service of res.data || []) {
                const meta = experimentLayerMetaFromService(service);
                result[meta.name] = meta;
            }
            return result;
        });
    }
    /**
     * Creates (first call for a layer) or updates a layer's display name,
     * permitted eligibility criteria, permitted bucketing entity types, and
     * required review groups. Does not accept activeDefaultsPr -- that's
     * managed by the layer-defaults PR-approval workflow, not this admin CRUD.
     * POST /campaign-management/v1/experiment-layer-meta
     */
    upsertExperimentLayerMeta(meta) {
        return axios
            .post(this.experimentLayerMetaUrl, experimentLayerMetaToService(meta), { headers: { "Content-Type": "application/json" } })
            .then(({ data: res }) => experimentLayerMetaFromService(res.data));
    }
    /**
     * Fetches per-layer segment-space and admission availability for the
     * given layers: current/permitted bucketing entity types, free segment
     * capacity and intervals, current reservation holders, and
     * (visibility-only) opt-in/forced share -- everything needed to validate a
     * proposed sampled experiment before it's saved. Not cached: served from
     * experiment_layer_meta/experiment_layer_reservation rather than a scan of
     * the experiment table, so it's safe to call on every keystroke-debounced
     * validation pass, and the whole point is a live view of current
     * reservations.
     * GET /campaign-management/v1/experiment-layer-availability
     */
    getLayerAvailability(layers, candidateBucketingEntityType) {
        return axios
            .get(this.layerAvailabilityUrl, {
            params: {
                layers: layers.join(","),
                candidate_bucketing_entity_type: candidateBucketingEntityType
            },
            headers: { "Content-Type": "application/json" }
        })
            .then(({ data: res }) => {
            const result = {};
            for (const service of res.data || []) {
                const availability = layerAvailabilityFromService(service);
                result[availability.layer] = availability;
            }
            return result;
        });
    }
    /**
     * Previews the schedule a SWITCHBACK experiment would run, relative to launch:
     * one cycle's legs and gaps, per-arm time, hour-of-day coverage, a recommended
     * minimum length and slot/gap timing, and warnings. Takes unsaved form state,
     * sent exactly as save() would send it but without save's checks, or a saved
     * experiment's ID. Read-only and not cached.
     * POST /campaign-management/v1/experiment-switchback-preview
     */
    async previewSwitchbackSchedule(source) {
        let body;
        if (typeof source === "string") {
            const experimentId = source.trim();
            if (!experimentId) {
                throw new ExperimentBadRequestError("An experiment ID is required");
            }
            body = { experiment_id: experimentId };
        }
        else {
            body = { experiment: await this.toServiceExperiment(source) };
        }
        return axios
            .post(this.switchbackPreviewUrl, body, { headers: { "Content-Type": "application/json" } })
            .then(({ data: res }) => switchbackSchedulePreviewFromService(res.data || {}));
    }
    /**
     * Proposes a new default configuration for a layer by opening a GitHub pull
     * request for review, the same review flow used for experiments. Does NOT
     * write the configuration directly -- see insertDefaultConfiguration for
     * that -- so the getDefaultConfigurations() cache is left untouched.
     * POST /campaign-management/v1/experiment-layers/publish
     */
    publishDefaultConfiguration(layer, configuration) {
        return axios
            .post(`${this.configurationsUrl}/publish`, { layer, configuration }, { headers: { "Content-Type": "application/json" } })
            .then(({ data: res }) => res.data);
    }
    /**
     * Fetches every team in the GitHub org default-configuration and
     * experiment review PRs are opened against, for validating and
     * autocompleting experiment layer meta's required review groups. Cached
     * for the life of this SDK instance -- see githubTeamsPromise.
     * GET /campaign-management/v1/github-teams
     */
    getGitHubTeams() {
        if (this.githubTeamsPromise) {
            return this.githubTeamsPromise;
        }
        this.githubTeamsPromise = axios
            .get(this.githubTeamsUrl, {
            headers: { "Content-Type": "application/json" }
        })
            .then(({ data: res }) => (res.data || []).map(gitHubTeamFromService))
            .catch((err) => {
            this.githubTeamsPromise = null;
            throw err;
        });
        return this.githubTeamsPromise;
    }
    /**
     * Fetches experiments from the campaign manager API, optionally filtered by
     * ID and/or a single advertiserId. advertiserId is passed to the API; ID
     * filtering is applied client-side, as the API has no per-ID lookup.
     */
    async findItems(filters, _sort) {
        validateFilterStructure(filters, [
            { filterType: FilterTypes.EQ, valueType: FilterValueTypes.STRING },
            { filterType: FilterTypes.IN, valueType: FilterValueTypes.OBJECT }
        ]);
        const idFilter = parseIdFilter(filters);
        const advertiserIdFilter = parseInclusionFilter(filters, "advertiserId", ["string"]);
        if (advertiserIdFilter.size > 1) {
            throw new ExperimentBadRequestError("Experiments can only be filtered by one advertiserId");
        }
        const advertiserId = advertiserIdFilter.values().next().value;
        // An empty advertiserId filter matches nothing, rather than every experiment.
        if (!advertiserId && filters?.where?.some(({ field }) => field === "advertiserId")) {
            return [];
        }
        const experiments = await this.getExperiments(advertiserId);
        if (idFilter.size === 1) {
            const id = idFilter.values().next().value;
            const experiment = experiments.find((exp) => exp.id === id);
            if (!experiment) {
                throw new ExperimentNotFoundError(id);
            }
            return [experiment];
        }
        if (idFilter.size > 1) {
            return experiments.filter((exp) => idFilter.has(exp.id));
        }
        return experiments;
    }
    /**
     * Fetch all experiments, or only those scoped to one advertiser, from the campaign manager API
     * GET /campaign-management/v1/experiments[?advertiser_id=...]
     */
    getExperiments(advertiserId) {
        const cacheKey = advertiserId
            ? `${this.collectionUrl}?advertiser_id=${advertiserId}`
            : this.collectionUrl;
        return this.cache.promise(cacheKey, () => axios
            .get(this.collectionUrl, {
            ...(advertiserId && { params: { advertiser_id: advertiserId } }),
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(({ data: res }) => {
            const experiments = (res.data || []).map(experimentFromService);
            // Also filtered here so a server that ignores advertiser_id can't widen the result.
            return advertiserId
                ? experiments.filter((exp) => exp.advertiserId === advertiserId)
                : experiments;
        }));
    }
    /**
     * Saves an experiment via the campaign manager API.
     * POST /campaign-management/v1/experiment
     */
    async saveItem(experiment) {
        const user = this.sdk.getCurrentUser();
        // At this time, dev@madhive.com is the only user that should be allowed to create or edit experiments.
        // In the future, we may try to restrict access by user and disallow the root Madhive user from modifying experiments.
        if (!user || user.email !== "dev@madhive.com") {
            throw new UnauthorizedError();
        }
        if (!experiment.creatorEmail) {
            throw new ExperimentBadRequestError("Creator email is required");
        }
        const errors = this.validate(experiment);
        if (errors.size) {
            const errorMessages = Array.from(errors.values()).join(", ");
            throw new ExperimentBadRequestError(errorMessages);
        }
        if ("id" in experiment && experiment.id) {
            await this.validateStatusTransition(experiment);
        }
        const serviceExperiment = await this.toServiceExperiment(experiment);
        return axios
            .post(this.singleUrl, serviceExperiment, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(({ data: res }) => {
            this.cache.remove(this.collectionUrl);
            return experimentFromService(res.data);
        });
    }
    /** The body save() sends: the experiment with its eligibility criteria resolved. */
    async toServiceExperiment(experiment) {
        let eligibilityCriteria = null;
        if ("eligibilityCriteriaId" in experiment && experiment.eligibilityCriteriaId) {
            eligibilityCriteria = await this.sdk.eligibilityCriteria.find_once({
                where: [
                    {
                        field: "id",
                        type: FilterTypes.EQ,
                        value: experiment.eligibilityCriteriaId
                    }
                ]
            });
        }
        return experimentToService(experiment, eligibilityCriteria);
    }
    /**
     * Looks up experiment entities by ID.
     * GET /campaign-management/v1/experiment-entities?ids=id1,id2,...
     * Unresolvable IDs are returned with an `error` field rather than being dropped.
     */
    async getExperimentEntities(ids) {
        if (ids.length === 0) {
            return [];
        }
        return axios
            .get(this.entitiesUrl, {
            params: { ids: ids.join(",") },
            headers: { "Content-Type": "application/json" }
        })
            .then(({ data: res }) => (res.data || []).map(experimentEntityFromService));
    }
    async deleteItem(_id) {
        throw new NotImplementedError("Delete is not supported for experiments");
    }
    /**
     * Validates that a status transition is allowed for an existing experiment.
     * Fetches the current experiment state and checks the transition against the allowed map.
     */
    async validateStatusTransition(experiment) {
        const experiments = await this.getExperiments();
        const existing = experiments.find((exp) => exp.id === experiment.id);
        if (!existing) {
            throw new ExperimentNotFoundError(experiment.id);
        }
        if (experiment.status === existing.status) {
            return;
        }
        if (!isValidStatusTransition(existing.status, experiment.status)) {
            const validNextStatuses = getValidNextStatuses(existing.status);
            throw new ExperimentBadRequestError(`Invalid status transition from "${existing.status}" to "${experiment.status}". ` +
                `Valid transitions from "${existing.status}": ${validNextStatuses.length ? validNextStatuses.join(", ") : "none (terminal state)"}`);
        }
    }
}
export default Experiments;
//# sourceMappingURL=index.js.map